/**
 *  mpc-sibling-swap — the Style (sibling) swatches switch the product IN PLACE (owner ruling V11).
 *
 *  Distinctive renders each sibling colour as a plain <a href> to another product
 *  (snippets/product-siblings.liquid), so a click reloads the whole page. This file turns that
 *  click into: fetch the sibling's page, swap every section inside <main>, push the URL. The link
 *  itself is untouched, so no JS, a modifier/middle click, a failed fetch, or a page whose layout
 *  differs all end in the ordinary navigation — the swap is an enhancement, never the only path.
 *
 *  Rules:
 *   - product pages only, links inside <main> only (quick view and featured-product keep navigating);
 *   - the fetched <main> must carry the SAME section ids in the SAME order (same template), or we
 *     navigate instead of guessing;
 *   - <modal-dialog> elements move themselves to <body> on connect (app.js), so the OLD product's
 *     sticky add-to-cart modal would survive the swap with the same id as the new one — and
 *     getElementById would hand back the old product's form. They are removed before the swap;
 *   - Back/Forward after a swap reloads the page: the server is the truth, no DOM history kept;
 *   - scripts inside swapped sections are NOT re-executed: the theme is custom elements (they
 *     upgrade on insertion) and its assets are already loaded. So a page that needs a load-time
 *     script navigates instead: an incoming Judge.me widget with reviews, or any <script src> this
 *     document never loaded (an app block).
 */
(function () {
  if (window.MpcSiblingSwap) return;

  const MAIN_ID = 'main-content';
  const LINK = '.variations--siblings a[href]';
  const FRESH_MS = 60000;
  const cache = new Map(); // url -> { at, promise }
  let busy = false;
  let swapped = false;

  const load = (url) => {
    const hit = cache.get(url);
    if (hit && Date.now() - hit.at < FRESH_MS) return hit.promise;
    const promise = fetch(url, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.text() : null))
      .catch(() => null);
    cache.set(url, { at: Date.now(), promise });
    return promise;
  };

  const sections = (main) => Array.from(main.children).filter((el) => el.classList.contains('shopify-section'));

  // Returns true when the page was swapped; false means "navigate normally".
  const swap = (url, html) => {
    if (!html) return false;
    const next = new DOMParser().parseFromString(html, 'text/html');
    const nextMain = next.getElementById(MAIN_ID);
    const main = document.getElementById(MAIN_ID);
    if (!nextMain || !main) return false;

    const current = sections(main);
    const incoming = sections(nextMain);
    if (!current.length || current.length !== incoming.length) return false;
    if (current.some((section, index) => section.id !== incoming[index].id)) return false;

    // Apps that only initialise on a real page load cannot follow an in-place swap, so those pages
    // navigate instead. Observed on the draft 2026-09-20: a product WITH Judge.me reviews came back
    // with its review widget hidden (the widget ships display:none until Judge.me's load-time
    // script reveals it, and jdgm.initializeWidgets() throws when called again). Generic net: a
    // <script src> inside the incoming <main> that this document never loaded is an app block or
    // asset we have not run.
    const reviews = nextMain.querySelector('.jdgm-rev-widg');
    if (reviews && Number(reviews.getAttribute('data-number-of-reviews')) > 0) return false;
    const loaded = new Set(Array.from(document.querySelectorAll('script[src]')).map((script) => script.src.split('?')[0]));
    const foreign = Array.from(nextMain.querySelectorAll('script[src]')).some((script) => !loaded.has(script.src.split('?')[0]));
    if (foreign) return false;

    // The old product's body-level modals (sticky add to cart) must not outlive it.
    nextMain.querySelectorAll('modal-dialog[id]').forEach((modal) => {
      document.querySelectorAll(`body > modal-dialog[id="${modal.id}"]`).forEach((stale) => stale.remove());
    });
    document.body.classList.remove('overflow-hidden');

    current.forEach((section, index) => {
      section.innerHTML = incoming[index].innerHTML;
    });

    if (next.title) document.title = next.title;
    if (!swapped) window.history.replaceState({ mpcSibling: true }, '', window.location.href);
    window.history.pushState({ mpcSibling: true }, '', url);
    swapped = true;
    cache.delete(url);

    main.querySelector('.variations--siblings label.active a')?.focus({ preventScroll: true });
    document.dispatchEvent(new CustomEvent('mpc:sibling-swapped', { detail: { url } }));
    return true;
  };

  const siblingLink = (event) => {
    if (!document.body.classList.contains('template-product')) return null;
    const link = event.target.closest ? event.target.closest(LINK) : null;
    return link && link.closest(`#${MAIN_ID}`) ? link : null;
  };

  const onClick = (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = siblingLink(event);
    if (!link) return;
    event.preventDefault();
    if (busy || link.closest('label')?.classList.contains('active')) return;
    busy = true;
    const url = link.href;
    const fieldset = link.closest('fieldset');
    fieldset?.setAttribute('aria-busy', 'true');
    load(url).then((html) => {
      let done = false;
      try {
        done = swap(url, html);
      } catch (error) {
        done = false;
      }
      busy = false;
      fieldset?.removeAttribute('aria-busy');
      if (!done) window.location.href = url;
    });
  };

  const onIntent = (event) => {
    const link = siblingLink(event);
    if (link) load(link.href);
  };

  document.addEventListener('click', onClick);
  document.addEventListener('mouseover', onIntent, { passive: true });
  document.addEventListener('touchstart', onIntent, { passive: true });
  document.addEventListener('focusin', onIntent);
  // After a swap the DOM no longer matches the history entry being restored: ask the server.
  window.addEventListener('popstate', () => {
    if (swapped) window.location.reload();
  });

  window.MpcSiblingSwap = { swap, onClick };
})();
